'use client'

import { $autoinspectorApi } from '@autoinspector/providers/autoinspector'
import { useApiError } from '@detail/hooks/useApiError'
//@ts-ignore
import { zodResolver } from '@hookform/resolvers/zod'
import {
  LoginBouncedRequestQuery,
  loginUserRequestBody,
} from 'bff/schemas/auth'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from 'ui/button'
import { Checkbox } from 'ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'ui/form'
import { Input } from 'ui/input'
import { z } from 'zod'
import {
  SignInShell,
  SignInShellHeader,
  SignInShellHeaderDescription,
  SignInShellHeaderTitle,
  SignInShellMoreOptions,
  SignInShellSocialProviders,
  SignMethod,
} from '../guest/[guestId]/shell'
import Link from 'next/link'
import { useToast } from 'ui/use-toast'
import { useTranslation } from 'i11n/client'
import { useCallbackError } from 'hooks/useCallbackError'
import { useGoogleOneTap } from 'hooks/useGoogleOneTap'
import { useError } from 'hooks/useError'
import { SignInMethod } from 'bff'

declare global {
  interface Window {
    google: typeof import('google-one-tap')
  }
}

const schema = loginUserRequestBody

type FormData = z.infer<typeof schema>

const EmailSignIn = ({
  setSignMethod,
  lastSignInMethod,
}: {
  setSignMethod: (method: SignMethod) => void
  lastSignInMethod?: SignInMethod | null
}) => {
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  })

  const router = useRouter()

  const { handleError } = useApiError()

  const mutation = $autoinspectorApi.useMutation(
    'post',
    '/v1/account/user/login',
    {
      onSuccess: () => {
        router.push('/')
      },
      onError: handleError,
    },
  )

  const handleSubmit = useCallback((body: FormData) => {
    mutation.mutate({
      body,
    })
  }, [])

  return (
    <Form {...form}>
      <form
        className='space-y-4 mt-4'
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    placeholder='Email'
                    className='rounded-xl border-slate-500 focus-visible:ring-0'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )
          }}
        />

        <FormField
          control={form.control}
          name='password'
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type='password'
                    placeholder='Contraseña'
                    className='rounded-xl border-slate-500 focus-visible:ring-0'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )
          }}
        />

        <div className='flex flex-col justify-end items-end gap-1 self-stretch !mt-2'>
          <Link
            className='text-slate-600 font-space-grotesk text-xs-plus font-medium leading-5 underline decoration-skip-ink-none'
            href='/forgot-password'
          >
            Olvidé mi contraseña
          </Link>
        </div>

        <Button
          type='submit'
          loading={mutation.isPending || mutation.isSuccess}
          className='text-base w-full bg-primary text-white hover:bg-primary hover:text-white h-11 border-0 rounded-[.625rem]'
          size='lg'
        >
          Iniciar sesión
        </Button>

        <div className='flex justify-center items-center !mt-2'>
          <button
            className='text-slate-600 font-space-grotesk text-xs-plus font-medium leading-5 underline decoration-skip-ink-none'
            onClick={() => setSignMethod('idp')}
          >
            Volver
          </button>
        </div>
      </form>
    </Form>
  )
}

export const AuthenticateTOS = () => {
  return (
    <p className='text-slate-400 font-space-grotesk text-tiny font-medium mt-4'>
      Al hacer clic en Continuar, reconoce que ha leído y acepta los Términos de
      servicio y la Política de privacidad de Autoinspector.
    </p>
  )
}

export const LoginForm = ({
  lastSignInMethod,
}: {
  lastSignInMethod?: SignInMethod | null
}) => {
  const [signMethod, setSignMethod] = useState<SignMethod>('idp')

  const { catchErrors } = useCallbackError()

  const router = useRouter()

  const { handleError } = useApiError()

  const mutation = $autoinspectorApi.useMutation(
    'post',
    '/v1/account/user/idp/login',
    {
      onSuccess: () => router.push('/'),
      onError: handleError,
    },
  )

  const { prompt } = useGoogleOneTap({
    callback: (token) =>
      mutation.mutate({
        body: {
          provider: 'google',
          token,
        },
      }),
  })

  useEffect(() => {
    catchErrors()

    prompt()
  }, [])

  return (
    <SignInShell>
      <SignInShellHeader>
        <SignInShellHeaderTitle>Iniciar sesión</SignInShellHeaderTitle>
        <SignInShellHeaderDescription>
          Automatiza tus inspecciones: detecta fraudes y reduce costos. En
          minutos, no días.
        </SignInShellHeaderDescription>
      </SignInShellHeader>

      {signMethod === 'idp' ? (
        <>
          <div className='flex flex-col space-y-4 !mt-[1.5rem] w-78'>
            <SignInShellSocialProviders
              lastSignInMethod={lastSignInMethod}
              authConfig={{
                email: true,
                google: true,
                microsoft: true,
              }}
              endpoint='/v1/account/auth/oauth/authorization/url'
              params={{}}
            />
            <SignInShellMoreOptions
              lastSignInMethod={lastSignInMethod}
              setSignMethod={setSignMethod}
            />
          </div>

          <AuthenticateTOS />
        </>
      ) : (
        <EmailSignIn setSignMethod={setSignMethod} />
      )}
    </SignInShell>
  )
}
